import Phaser from 'phaser'
import TextureKeys from '../consts/TextureKeys'
import AnimationKeys from '../consts/AnimationKeys'
import SceneKeys from '../consts/SceneKeys'

enum MouseState {
  Running,
  Killed,
  Dead,
}

export default class RocketMouse extends Phaser.GameObjects.Container {
  private flames: Phaser.GameObjects.Sprite
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys
  private mouse: Phaser.GameObjects.Sprite

  private mouseState = MouseState.Running
  private canDropBomb = true
  private bombKey!: Phaser.Input.Keyboard.Key
  private canLaunchFlag = true
  private flagKey!: Phaser.Input.Keyboard.Key
  private isThrowingFlag = false
  private externalJetpackActive = false // For touch controls

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y)

    this.mouse = scene.add.sprite(0, 0, TextureKeys.RocketMouse).setOrigin(0.5, 1)

    // Position flames on the player's back (moved up and to the right)
    this.flames = scene.add.sprite(-40, -35, TextureKeys.RocketMouse)

    this.createAnimations()

    this.mouse.play(AnimationKeys.RocketMouseRun)
    this.flames.play(AnimationKeys.RocketFlamesOn)

    this.enableJetpack(false)

    this.add(this.flames)
    this.add(this.mouse)

    scene.physics.add.existing(this)

    const body = this.body as Phaser.Physics.Arcade.Body
    // Use a circular collision body for better coin collection
    const radius = this.mouse.height * 0.5
    body.setCircle(radius)
    body.setOffset(-radius, -this.mouse.height + (this.mouse.height * 0.5 - radius))

    this.cursors = scene.input.keyboard!.createCursorKeys()
    this.bombKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT)
    this.flagKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D)
  }

  canDropBombNow() {
    return this.canDropBomb
  }

  resetBombCooldown() {
    this.canDropBomb = true
  }

  isBombKeyPressed() {
    return Phaser.Input.Keyboard.JustDown(this.bombKey) && this.canDropBomb
  }

  setBombDropped() {
    this.canDropBomb = false
  }

  canLaunchFlagNow() {
    return this.canLaunchFlag
  }

  resetFlagCooldown() {
    this.canLaunchFlag = true
  }

  isFlagKeyPressed() {
    return Phaser.Input.Keyboard.JustDown(this.flagKey) && this.canLaunchFlag && !this.isThrowingFlag
  }

  setFlagLaunched() {
    this.canLaunchFlag = false
  }

  playThrowAnimation() {
    this.isThrowingFlag = true
    this.mouse.play(AnimationKeys.RocketMouseThrow)

    // Reset throwing flag after animation completes
    this.mouse.once('animationcomplete', () => {
      this.isThrowingFlag = false
    })
  }

  setExternalJetpack(active: boolean) {
    this.externalJetpackActive = active
  }

  enableJetpack(enabled: boolean) {
    this.flames.setVisible(enabled)
  }

  isDead() {
    return this.mouseState !== MouseState.Running
  }

  bounce() {
    // Give player a bounce when stomping on enemies
    const body = this.body as Phaser.Physics.Arcade.Body
    body.setVelocityY(-300)
  }

  kill() {
    if (this.mouseState !== MouseState.Running) {
      return
    }

    this.mouseState = MouseState.Killed

    this.mouse.play(AnimationKeys.RocketMouseDead)

    const body = this.body as Phaser.Physics.Arcade.Body
    body.setAccelerationY(0)
    body.setVelocity(1000, 0)
    this.enableJetpack(false)
  }

  preUpdate() {
    const body = this.body as Phaser.Physics.Arcade.Body

    switch (this.mouseState) {
      case MouseState.Running: {
        // Check both keyboard space AND external button press (touch controls)
        const jetpackActive = this.cursors.space?.isDown || this.externalJetpackActive

        if (jetpackActive) {
          // Increase upward acceleration for more powerful jetpack
          body.setAccelerationY(-1000)

          // If falling, immediately counter downward velocity for instant response
          if (body.velocity.y > 0) {
            body.setVelocityY(body.velocity.y * -0.4) // Reverse 40% of downward velocity upward
          }

          this.enableJetpack(true)

          if (!this.isThrowingFlag) {
            this.mouse.play(AnimationKeys.RocketMouseFly, true)
          }
        } else {
          // Apply 2x gravity when falling (normal gravity is 200, so 2x = 400 additional)
          if (body.velocity.y > 0) {
            body.setAccelerationY(600)
          } else {
            body.setAccelerationY(0)
          }
          this.enableJetpack(false)
        }

        // Only play movement animations if not throwing
        if (!this.isThrowingFlag) {
          if (body.blocked.down) {
            this.mouse.play(AnimationKeys.RocketMouseRun, true)
          } else if (body.velocity.y > 0) {
            this.mouse.play(AnimationKeys.RocketMouseFall, true)
          }
        }
        break
      }

      case MouseState.Killed: {
        body.velocity.x *= 0.92
        if (body.velocity.x <= 50) {
          this.mouseState = MouseState.Dead
        }
        break
      }

      case MouseState.Dead: {
        if (this.scene.scene.isActive(SceneKeys.GameOver)) {
          break
        }

        body.setVelocity(0, 0)
        // Get the death cause and score from the Game scene if available
        const gameScene = this.scene as any
        const deathCause = gameScene.deathCause || 'laser'
        const score = gameScene.score || 0
        this.scene.scene.run(SceneKeys.GameOver, { deathCause, score })
        break
      }
    }
  }

  private createAnimations() {
    this.mouse.anims.create({
      key: AnimationKeys.RocketMouseRun,
      frames: this.mouse.anims.generateFrameNames(TextureKeys.RocketMouse, {
        start: 1,
        end: 4,
        prefix: 'rocketmouse_run',
        zeroPad: 2,
        suffix: '.png',
      }),
      frameRate: 10,
      repeat: -1,
    })

    this.mouse.anims.create({
      key: AnimationKeys.RocketMouseFall,
      frames: [
        {
          key: TextureKeys.RocketMouse,
          frame: 'rocketmouse_fall01.png',
        },
      ],
    })

    this.mouse.anims.create({
      key: AnimationKeys.RocketMouseFly,
      frames: [
        {
          key: TextureKeys.RocketMouse,
          frame: 'rocketmouse_fly01.png',
        },
      ],
    })

    this.mouse.anims.create({
      key: AnimationKeys.RocketMouseDead,
      frames: this.mouse.anims.generateFrameNames(TextureKeys.RocketMouse, {
        start: 1,
        end: 2,
        prefix: 'rocketmouse_dead',
        zeroPad: 2,
        suffix: '.png',
      }),
      frameRate: 10,
    })

    // Create throw animation using the throw-item sprite sheet
    this.mouse.anims.create({
      key: AnimationKeys.RocketMouseThrow,
      frames: this.mouse.anims.generateFrameNames(TextureKeys.ThrowItem, {
        start: 1,
        end: 5,
        prefix: 'rocketmouse_throw',
        zeroPad: 2,
        suffix: '.png',
      }),
      frameRate: 15,
      repeat: 0,
    })

    this.flames.anims.create({
      key: AnimationKeys.RocketFlamesOn,
      frames: this.flames.anims.generateFrameNames(TextureKeys.RocketMouse, { start: 1, end: 2, prefix: 'flame', suffix: '.png' }),
      frameRate: 10,
      repeat: -1,
    })
  }
}
